import { CircularProgress, Popover, Tooltip } from "@mui/material";
import MDFeListingApi from "api/MDFeListingApi";
import { formatToBRL, formatToCPFOrCNPJ } from "@utils/formatters";
import MDFeFilterComponent from "components/filter/mdfeFilter";
import Modal from "components/modal/modal";
import Table, { Column, OrderInfo, TableAction } from "components/table/mdfeTable";
import { MDFeFilter } from "model/mdfe/MDFeFilter";
import { mdfeFilterStatus } from "model/mdfe/MDFeFilterStatus";
import { mdfeStatus } from "model/mdfe/MDFeStatus";
import { MDFe } from "model/mdfe/MDFe";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import styled from "styled-components";
import { useRef } from "react";
import { useLocation } from "react-router-dom";
import { readListingQuery } from "@utils/listingQuery";
import PageFrame from "components/page-frame/pageFrame";
import { getItem, setItem } from '@services/LocalStorageService';
import PDFViewer from "@components/pdf/pdfViewer";
import HistoryDetails from "@components/historyDetails/historyDetails";
import fileDownload from "js-file-download";
import { useAuth } from '@contexts/auth';
import { PERMISSIONS } from "@utils/permissions";
import PickList, { PickListItem } from "components/picklist/picklist";
import { cUFList } from "@components/edit-nfe/tabs/helper/nfeRefTabHelper";
import { format, parse } from "date-fns/esm";
import DatePicker from "react-date-picker";

const StyledFrame = styled.div`
    width: 100%;
    height: 100%;

    position: relative;

    display: flex;
    flex-direction: column;

    .centered {
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .body {
        width: 100%;
        height: 100%;
        max-height: 100%;

        display: flex;
        flex-direction: column;
        position: relative;

        .customize-columns-toggle {
            height: 32px;
            width: fit-content;

            padding: 0 8px;

            margin-left: auto;

            border: 1px solid var(--grey-light);
            border-radius: 8px;

            display: flex;
            align-items: center;
            justify-content: center;

            user-select: none;
            font-family: 'TitilliumWeb';

            cursor: pointer;

            background-color: var(--white);

            :active {
                background-color: var(--grey-lightest);
            }

            :focus-visible {
                outline: none;
            }

            > span {
                text-transform: uppercase;
                font-size: .7rem;
                font-weight: bold;
                letter-spacing: .3px;
            }
        }
    }

    .correcao-frame {
        display: flex;
        flex-direction: column;

        > span {
            margin-bottom: 12px;
        }

        > textarea {
            min-width: 550px;
            min-height: 70px;
        }
    }

    /* as listas e o calendário abrem para fora do corpo do modal: sem isso ficam cortados pelo overflow */
    .enc-modal .modal-body {
        overflow: visible;
    }

    .enc-frame {
        display: flex;
        flex-direction: column;
        min-width: 550px;
        /* acima dos botões do rodapé do Modal, que têm z-index 1001 */
        position: relative;
        z-index: 1002;

        > span {
            margin-bottom: 12px;
        }

        .enc-row {
            display: flex;
            align-items: flex-end;
            margin-bottom: 8px;

            > div {
                display: flex;
                flex-direction: column;
                margin-right: 12px;

                > small {
                    color: var(--grey-regular);
                    margin-bottom: 2px;
                }
            }
        }

        #picklist {
            line-height: 25px;
        }

        .enc-uf #picklist {
            .overlay.is-opened {
                height: 200px;
            }
            .body-section.is-opened {
                height: 150px;
            }
        }

        .enc-city-placeholder {
            height: 30px;
            width: 260px;
            box-sizing: border-box;
        }
    }

    .hist-item-frame {
        height: fit-content;
        display: flex;
        align-items: top;

        .data-text {
            white-space: nowrap;
            margin-right: 16px;
        }

        .status-indicator {
            height: 6px;
            width: 6px;
            border-radius: 50%;
            margin-top: 6px;
            margin-left: 8px;
            margin-right: 8px;
            margin-bottom: 6px;
            border: 3px solid var(--color-success);
        }

        .status-link-bar {
            width: 3px;
            min-height: 100%;
            background-color: var(--color-success);
            border-radius: 6px;
            margin-top: 7px;
            margin-left: 12px;
            margin-right: 12px;
        }
    }

    .div-table-cell {
        display: table-cell;
        vertical-align: top;
        padding-left: 10px;
        padding-bottom: 10px;
        padding-top: 15px;
    }

    .line-separator {
        display: table-cell;
        border-left: none;
        border-top: var(--grey-light);
    }
`;

const ModalFrame = styled.div`
        top: 6px;
        left: -14px;
        width: 100%;
        position: absolute;
        transform: translateY(-96px);
        z-index: 1000;
`;

const PAGE_SIZE = 30;

/* cUF do evEncMDFe e TCodUfIBGE_EX: 99 e o encerramento no exterior, sempre com cMun 9999999 (MOC K04) */
const COD_UF_EXTERIOR = '99';
const COD_MUN_EXTERIOR = '9999999';

const closureUFList: PickListItem[] = [...cUFList, { id: 99, codigo: COD_UF_EXTERIOR, value: 'EX' }];

/* status da contingência offline: o XML de autorização (mdfeProc) só existe depois da transmissão */
const OFFLINE_STATUSES: string[] = ['MDFE_OFFLINE', 'ERRO_OFFLINE'];

interface PageAndSort {
    sort: OrderInfo;
    page: number;
}

/* campos editáveis do encerramento: a data já fica como AAAA-MM-DD (TData do evEncMDFe), que é o valor do input date */
interface ClosureForm {
    closureDate: string,
    stateCode: string,
    cityCode: string
}

/* sugere a UF de fim do percurso: é onde o MDFe normalmente se encerra, mas o usuário pode trocar */
const initialClosure = (mdfe: MDFe): ClosureForm => ({
    closureDate: format(new Date(), 'yyyy-MM-dd'),
    stateCode: cUFList.find(uf => uf.value === mdfe.endUf)?.codigo || '',
    cityCode: ''
});

/* no MDF-e o modal tem um digito (1 a 4), diferente dos dois do CT-e */
const getModalLabel = (modal?: string) => {
    switch (modal) {
        case '1': return 'Rodoviário';
        case '2': return 'Aéreo';
        case '3': return 'Aquaviário';
        case '4': return 'Ferroviário';
        default: return modal;
    }
}

const getFilterStatus = (statusValue: string) => {
    const auxStatus = mdfeFilterStatus.filter(s => s.value?.toUpperCase() === statusValue.toUpperCase())[0];
    if (auxStatus) {
        return auxStatus;
    }
}

/**
    * Builds the status layout for each individual MDFe item on the list with the apropriate color
    * @param mdfeItem represents the MDFe being render on screen
    * @returns a custom JSX element for a MDFe's status
    */
const getStatusLayout = (mdfeItem: MDFe) => {

    if (mdfeItem.reportStatus) {

        let status = getFilterStatus(mdfeItem.reportStatus);

        /* o grupo esconderia a contingência (Emitido/Falha na emissão): o offline ainda precisa ser
           transmitido, então mostra o status dele, com a cor do grupo */
        const label = OFFLINE_STATUSES.includes(mdfeItem.status || '')
            ? mdfeStatus.find(st => st.value === mdfeItem.status)?.label
            : status?.label;

        return <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{
                borderRadius: '50%',
                width: '6px',
                height: '6px',
                marginRight: '6px',
                backgroundColor: status?.refColor
            }}></div>
            <span>{label}</span>
        </div>;
    }

    return <div>Status Error: {mdfeItem.status}</div>;
}

const MDFeListPage = () => {

    const [columns, setColumns] = useState<Column[]>([
        { header: 'CNPJ Emitente', accessor: 'company.cnpj', isActive: true, minWidth: 125, customView: (value: any) => value.issuerCnpj },
        { header: 'ID Transação', accessor: 'transactionId', isActive: false, minWidth: 210 },
        { header: 'Data de emissão', accessor: 'issueDate', isActive: true, minWidth: 130 },
        { header: 'Nº Documento', accessor: 'docNum', isActive: true, minWidth: 80 },
        { header: 'Série', accessor: 'series', isActive: true, minWidth: 35 },
        { header: 'Valor da carga', accessor: 'cargoAmount', isActive: true, minWidth: 95 },
        { header: 'Status', accessor: 'reportStatus', orderAccessor: "status", isActive: true, minWidth: 160, customView: getStatusLayout },
        { header: 'Chave de Acesso', accessor: 'accessKey', orderAccessor: "accessKey", isActive: false, minWidth: 340 },
        { header: 'Modal', accessor: 'modal', isActive: true, minWidth: 85, customView: (value: MDFe) => getModalLabel(value.modal) },
        { header: 'UF Início', accessor: 'startUf', isActive: true, minWidth: 45 },
        { header: 'UF Fim', accessor: 'endUf', isActive: true, minWidth: 45 },
        { header: 'Qtd. NFe', accessor: 'nfeQuantity', isActive: true, minWidth: 55 },
        { header: 'Qtd. CTe', accessor: 'cteQuantity', isActive: true, minWidth: 55 },
        { header: 'Data de encerramento', accessor: 'closureDate', isActive: false, minWidth: 130 },
    ]);

    const listingApi = new MDFeListingApi();

    const initialRender = useRef(true);

    const [hasMore, setHasMore] = useState(true);

    /* "Abrir na listagem" do cockpit chega com ?chave= ou ?transactionId= */
    const { search } = useLocation();
    const [filters, setFilters] = useState(() => {
        const { accessKey, transactionId } = readListingQuery(search);
        return Object.assign(new MDFeFilter(), { chaveMdfe: accessKey, ids: transactionId });
    });

    const [data, setData] = useState<MDFe[]>([]);

    const [pageAndSort, setPageAndSort] = useState<PageAndSort>({ sort: { columnOrdered: columns[2], direction: "DESC" }, page: 0 })

    const [totalAmountMDFes, setTotalAmountMDFes] = useState(0);

    const [selectedMDFes, setSelectedMDFes] = useState<MDFe[]>([]);

    const [activeMDFe, setActiveMDFe] = useState<MDFe>();

    const [downloadOptionsOpen, setDownloadOptionsOpen] = useState(false);

    const [visualizarDamdfeOpen, setVisualizarDamdfeOpen] = useState(false);

    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

    const [closureDialogOpen, setClosureDialogOpen] = useState(false);

    const [mdfeHistoryDialogOpen, setMdfeHistoryDialogOpen] = useState(false);

    const [closure, setClosure] = useState<ClosureForm>({ closureDate: '', stateCode: '', cityCode: '' });

    const [closureCities, setClosureCities] = useState<PickListItem[]>([]);
    const [loadingCities, setLoadingCities] = useState<boolean>(false);

    const [loadingEncerramento, setLoadingEncerramento] = useState<boolean>(false);
    const [loadingDownload] = useState<boolean>(false);
    const [loadingCancel, setLoadingCancel] = useState<boolean>(false);
    const [loadingHistory, setLoadingHistory] = useState<boolean>(false);
    const [exportingMDFes, setExportingMDFes] = useState<boolean>(false);
    const [downloadingXml, setDownloadingXml] = useState<boolean>(false);

    const { authInfo } = useAuth();
    const [pdf, setPdf] = useState<any>();
    const [pdfFilename, setPdfFilename] = useState<any>();

    const justificationRef = useRef(null);

    const mdfeActiveColumn: string = `mdfe-columns-active-${authInfo?.username}`

    const validDownloadStatuses: string[] = ['AUTORIZADO', 'CANCELADO', 'ENCERRADO', 'MDFE_OFFLINE', 'ERRO_OFFLINE', 'ERRO_ENVIO', 'ERRO_SEFAZ', 'REJEITADO_SEFAZ'];

    const actions = useMemo<TableAction[]>(() => [
        {
            html: <i className="bi bi-download table-action action-positive"></i>,
            callback: (event: Event, mdfe: MDFe) => { return handleDownloadClick(event, mdfe); },
            disabled: (actionTooltipText: string, statusLabel: string) => disableAction(actionTooltipText, statusLabel),
            tooltipText: 'Baixar arquivos',
            requiredPermissions: [PERMISSIONS.DOOTAX.MDFE.GERENCIAR.VISUALIZAR]
        },
        {
            html: <i className="bi bi-clock-history table-action action-positive" />,
            callback: (event: Event, mdfe: MDFe) => handleShowMDFeHistory(event, mdfe),
            disabled: (actionTooltipText: string, statusLabel: string) => disableAction(actionTooltipText, statusLabel),
            tooltipText: 'Visualizar histórico',
            requiredPermissions: [PERMISSIONS.DOOTAX.MDFE.GERENCIAR.VISUALIZAR]
        },
        {
            html: <i className="bi bi-x-lg table-action action-negative" />,
            callback: (event: Event, mdfe: MDFe) => handleShowCancelDialog(event, mdfe),
            disabled: (actionTooltipText: string, statusLabel: string) => disableAction(actionTooltipText, statusLabel),
            tooltipText: 'Cancelar MDFe',
            requiredPermissions: [PERMISSIONS.DOOTAX.MDFE.GERENCIAR.CANCELAR]
        },
        {
            html: <i className="bi bi-flag table-action action-positive" />,
            callback: (event: Event, mdfe: MDFe) => handleShowClosureDialog(event, mdfe),
            disabled: (actionTooltipText: string, statusLabel: string) => disableAction(actionTooltipText, statusLabel),
            tooltipText: 'Encerrar MDFe',
            requiredPermissions: [PERMISSIONS.DOOTAX.MDFE.GERENCIAR.ENCERRAR]
        },
    ], []);

    useEffect(() => {
        const savedColumnsConfig = getItem(mdfeActiveColumn);
        if (savedColumnsConfig) {
            setColumns(columns.map(col => ({ ...col, isActive: savedColumnsConfig.includes(col.accessor) })));
        } else {
            setItem(mdfeActiveColumn, columns.map(col => col.accessor).join(','));
        }
    }, []);

    const setFiltersAndResetPageNum = (updatedFilter: MDFeFilter) => {
        setFilters(updatedFilter);
        setPageAndSort({ ...pageAndSort, page: 0 });
    }

    const executeSearch = ({ concatResults }: { concatResults: boolean }) => {

        const { sort: sortInfo, page: pageIndex } = pageAndSort;
        listingApi.fetchFilteredMDFes(filters, pageIndex, PAGE_SIZE, sortInfo.columnOrdered.orderAccessor || sortInfo.columnOrdered.accessor, sortInfo.direction).then(({ content, totalElements, last }) => {
            setTotalAmountMDFes(totalElements);
            setHasMore(!last);

            const newData = content.map(mdfe => ({
                ...mdfe,
                companyId: (mdfe as any).company.id,
                issuerCnpj: formatToCPFOrCNPJ((mdfe as any).company.cnpj),
                cargoAmount: formatToBRL(mdfe.cargoAmount || '0'),
                isSelected: false,
                disableSelection: !validDownloadStatuses.includes(mdfe.status!)
            }));

            setData(concatResults ? [...data, ...newData] : newData);
        });
    }

    const handleFilter = useCallback(() => executeSearch({ concatResults: false }), [filters, pageAndSort]);

    useEffect(() => {

        if (initialRender.current) {
            initialRender.current = false;
        } else {
            executeSearch({ concatResults: pageAndSort.page !== 0 });
        }
    }, [pageAndSort]);

    useEffect(() => {
        selectedMDFes.length && setData(data.map(item => {
            return {
                ...item,
                isSelected: (selectedMDFes.find(i => (i as any).id === item.id) !== undefined)
            }
        }));
    }, [selectedMDFes]);

    /**
     * Checks if an action should be disabled for a specific MDFe based on it's status
     * @param tooltipText
     * @param statusLabel
     * @returns true if the action should be enabled for a specific MDFe
     */
    const disableAction = (tooltipText: string, statusLabel: string) => {
        const statusLower = (statusLabel || '').toLowerCase();
        switch (tooltipText.toLowerCase()) {
            /* cancelamento e encerramento exigem o MDFe autorizado (MDFeService.getMDFeApprovedByTransactionIdAndAccessKey):
               o offline só aceita evento depois de transmitido */
            case "cancelar mdfe":
                return statusLower !== "autorizado";

            case "encerrar mdfe":
                return statusLower !== "autorizado";

            /* os que têm DAMDFE (MDFeService.getMDFeWithDamdfeByTransactionIdAndAccessKey) */
            case "baixar arquivos":
                return !(statusLower === "autorizado" || statusLower === "cancelado" || statusLower === "encerrado"
                    || statusLower === "emissão offline" || statusLower === "erro autorização offline");

            default:
                return false;
        }
    }

    const handleExportMDFes = () => {
        setExportingMDFes(true);

        listingApi.exportMDFes(filters)
            .then(() => toast.success('MDFe\'s exportados com sucesso!'))
            .finally(() => setExportingMDFes(false));
    }

    const handleDownloadClick = (event: Event, mdfe: MDFe) => {
        setActiveMDFe(mdfe);
        setDownloadOptionsOpen(true);
        event.stopPropagation();
    }

    /* imprime por um iframe oculto: no lote o PDF chega depois do clique, e um window.open
       fora do gesto do usuario e bloqueado como pop-up (devolve null) */
    const handlePrint = (pdfBytes: Blob) => {
        const pdfUrl = URL.createObjectURL(pdfBytes);
        const frame = document.createElement('iframe');
        frame.style.display = 'none';
        frame.src = pdfUrl;
        frame.onload = () => {
            frame.contentWindow?.focus();
            frame.contentWindow?.print();
            /* o dialogo de impressao segura o conteudo; remove so depois, com folga */
            setTimeout(() => {
                document.body.removeChild(frame);
                URL.revokeObjectURL(pdfUrl);
            }, 60000);
        };
        document.body.appendChild(frame);
    }

    const handleVisualizarDamdfeClick = (mdfe: MDFe) => {
        setActiveMDFe(mdfe);

        listingApi.downloadDamdfe(mdfe).then(res => {
            if (res) {
                setPdf(res.data);
                setPdfFilename(res.headers.filename);
            } else {
                setPdf(undefined);
                setPdfFilename(undefined);
            }
        });

        setVisualizarDamdfeOpen(true);
        setDownloadOptionsOpen(false);
    }

    const handleShowMDFeHistory = (event: Event, mdfe: MDFe) => {
        if (!mdfe || !mdfe.id) {
            toast.error('É necessário selecionar um MDFe para visualizar seu histórico.');
            return;
        }

        setMdfeHistoryDialogOpen(true);
        setLoadingHistory(true);

        listingApi.fetchMDFeHistory(mdfe.id)
            .then(res => {
                setLoadingHistory(false);
                setActiveMDFe({ ...mdfe, history: res.data });
            }).catch(() => setLoadingHistory(false));

        event.stopPropagation();
    }

    const handleShowCancelDialog = (event: Event, mdfe: MDFe) => {
        setActiveMDFe(mdfe);
        mdfe && setCancelDialogOpen(true);
        event.stopPropagation();
    }

    /* carrega os municípios a cada troca de UF do encerramento; no exterior o município é fixo (COD_MUN_EXTERIOR) */
    useEffect(() => {
        setClosureCities([]);

        if (!closureDialogOpen || !closure.stateCode || closure.stateCode === COD_UF_EXTERIOR) {
            setLoadingCities(false);
            return;
        }

        let active = true;
        setLoadingCities(true);

        listingApi.fetchCities(closure.stateCode)
            .then(cities => active && setClosureCities(cities.map(city => ({ id: Number(city.code), codigo: city.code, value: city.name }))))
            .catch(() => { })
            .finally(() => active && setLoadingCities(false));

        return () => { active = false; };
    }, [closure.stateCode, closureDialogOpen]);

    const handleShowClosureDialog = (event: Event, mdfe: MDFe) => {
        setActiveMDFe(mdfe);
        setClosure(initialClosure(mdfe));
        mdfe && setClosureDialogOpen(true);
        event.stopPropagation();
    }

    /**
     * Valida o encerramento com a forma do evEncMDFe_v3.00.xsd (a mesma do backend). O código IBGE do
     * município começa pelo código da UF: a checagem evita uma rejeição que conta para o consumo indevido
     * @returns a mensagem do primeiro problema encontrado, ou undefined se estiver tudo certo
     */
    const validateClosure = ({ closureDate, stateCode, cityCode }: ClosureForm) => {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(closureDate)) {
            return 'A data de encerramento deve ser informada.';
        }
        if (!stateCode) {
            return 'A UF de encerramento deve ser informada.';
        }
        if (!/^\d{7}$/.test(cityCode)) {
            return 'O município de encerramento deve ser selecionado.';
        }
        if (stateCode === COD_UF_EXTERIOR && cityCode !== COD_MUN_EXTERIOR) {
            return `No encerramento no exterior, o código do município deve ser ${COD_MUN_EXTERIOR}.`;
        }
        if (stateCode !== COD_UF_EXTERIOR && !cityCode.startsWith(stateCode)) {
            return `O código IBGE do município deve começar pelo código da UF selecionada (${stateCode}).`;
        }
    }

    /**
     * Sends the closure event for a specified MDFe
     * @param mdfe MDFe to be closed
     */
    const handleCloseMDFe = (mdfe: MDFe) => {
        const error = validateClosure(closure);

        if (error) {
            toast.warning(error);
            return;
        }

        setLoadingEncerramento(true);

        listingApi.closeMDFe(mdfe, closure)
            .then(() => {
                setLoadingEncerramento(false);
                toast.success("MDFe encerrado com sucesso!");
                setClosureDialogOpen(false);
                handleFilter();
            })
            .catch(() => setLoadingEncerramento(false));
    }

    const handleCancelMDFe = (mdfe: MDFe) => {
        const textValue = (justificationRef.current as any).value as string;

        if (!textValue || textValue.length < 15 || textValue.length > 255) {
            toast.warning(`A justificativa deve conter entre 15 e 255 caracteres. Quantidade atual: ${textValue.length}`, { autoClose: 3500 });
            return;
        }

        setLoadingCancel(true);

        listingApi.cancelMDFe(mdfe, textValue)
            .then(() => {
                setLoadingCancel(false);
                toast.success("MDFe cancelado com sucesso!");
                setCancelDialogOpen(false);
                handleFilter()
            }).catch(() => setLoadingCancel(false));
    }

    const handleToggleColumn = (column: Column) => {
        const savedColumnsConfig = getItem(mdfeActiveColumn)?.split(',');

        if (!savedColumnsConfig || !savedColumnsConfig.length) {
            setItem(mdfeActiveColumn, column.accessor);
        } else {
            if (savedColumnsConfig.includes(column.accessor)) {
                savedColumnsConfig.splice(savedColumnsConfig.indexOf(column.accessor), 1);
            } else {
                savedColumnsConfig.unshift(column.accessor);
            }

            setItem(mdfeActiveColumn, savedColumnsConfig ? savedColumnsConfig.join(',') : '');
        }
    }

    /**
     * Shows/hides a table column selected by the user
     * @param column selected/unselected column
     */
    const shouldActivateColumn = (column: Column) => {
        setColumns(columns.map(col => {
            if (column.accessor === col.accessor) {
                col.isActive = !column.isActive
                handleToggleColumn(col);
            }
            return { ...col };
        }))
    }

    const [actionsEl, setActionsEl] = useState<HTMLButtonElement | null>(null);

    const handleActionsBtnClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setActionsEl(event.currentTarget);
    };

    const handleClose = () => {
        setActionsEl(null);
    };

    const open = Boolean(actionsEl);
    const id = open ? 'simple-popover' : undefined;

    const customizeColumnsBtn = <>
        <button
            className="customize-columns-toggle"
            aria-describedby={id}
            onClick={(event) => handleActionsBtnClick(event)}>
            <span>Personalizar colunas</span>
            <i className="bi bi-caret-down-fill" style={{ fontSize: '.75rem', marginLeft: '8px' }} />
        </button>
        <Popover
            id={id}
            open={open}
            anchorEl={actionsEl}
            onClose={handleClose}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
            }}>
            <div className='customize-columns-menu'>
                <div className="menu-header">
                    <span>Selecione as colunas que deseja exibir na lista:</span>
                </div>
                <div className="menu-items-frame">
                    {
                        columns.slice().sort((a, b) => {
                            if (a.header === b.header) return 0;
                            if (a.header > b.header) return 1;
                            if (a.header < b.header) return -1;
                            return 0;
                        }).map((col, index) => {
                            return <div key={index} style={{ width: 'fit-content', display: 'flex', padding: '10px', alignItems: 'center' }}>
                                <input type="checkbox"
                                    checked={col.isActive}
                                    onChange={() => shouldActivateColumn(col)}
                                />
                                <span>{col.header}</span>
                            </div>
                        })
                    }
                </div>
            </div>
        </Popover>
    </>;

    const handleSortByColumn = (col: Column) => {
        const { sort: sortInfo } = pageAndSort;

        setPageAndSort({
            page: 0,
            sort: {
                columnOrdered: col,
                direction: (col.accessor !== sortInfo.columnOrdered.accessor) || sortInfo.direction === "DESC" ? "ASC" : "DESC"
            }
        });
    }

    const handleDownloadXml = (mdfe: MDFe) => {
        if (!mdfe) {
            toast.error('É necessário selecionar um MDFe para realizar o download do XML.');
            return;
        }

        setDownloadingXml(true);

        listingApi.downloadXml(mdfe).then(res => {
            if (res && res.status === 204) {
                toast.info("Não foi encontrado nenhum xml para este MDFe.");
            }
        }).finally(() => setDownloadingXml(false));
    }

    const handleDownloadXmlBatch = (ids: number[]) => {
        if (!ids || !ids.length) {
            toast.warning('É necessário selecionar um MDFe para realizar o download do XML.');
            return;
        }

        listingApi.downloadXmlBatch(ids).then(res => {
            if (!res || !res.data || res.data.size === 0 || res.status !== 200) {
                toast.info("Não foi encontrado nenhum XML válido para os MDFes selecionados.");
            } else {
                fileDownload(res.data, res.headers.filename)
            }
        });
    }

    const handleDownloadDamdfeBatch = (ids: number[]) => {
        if (!ids || !ids.length) {
            toast.warning('É necessário selecionar um MDFe para realizar o download do DAMDFE.');
            return;
        }

        listingApi.downloadDamdfeBatch(ids).then(res => {
            if (!res || !res.data || res.data.size === 0 || res.status !== 200) {
                toast.info("Não foi possível gerar nenhum DAMDFE para os MDFes selecionados.");
            } else {
                fileDownload(res.data, res.headers.filename)
            }
        });
    }

    const handleDownloadDamdfeMerged = (ids: number[]) => {
        if (!ids || !ids.length) {
            toast.warning('É necessário selecionar um MDFe para realizar a impressão do DAMDFE.');
            return;
        }

        listingApi.downloadDamdfeMerged(ids).then(res => {
            if (!res || !res.data || res.data.size === 0 || res.status !== 200) {
                toast.info("Não foi possível gerar nenhum DAMDFE para os MDFes selecionados.");
            } else {
                handlePrint(res?.data);
            }
        });
    }

    const activeIsOffline = OFFLINE_STATUSES.includes(activeMDFe?.status || '');

    // ----------------------------
    // RETURN
    // ----------------------------
    return <PageFrame><StyledFrame>
        <section className="body">
            <div style={{ width: '100%', display: "flex", marginBottom: '12px' }}>
                <MDFeFilterComponent model={filters} onFiltersChange={(updatedFilter: MDFeFilter) => setFiltersAndResetPageNum(updatedFilter)} reset={() => setFiltersAndResetPageNum(new MDFeFilter())} />
                <button
                    className="customize-columns-toggle"
                    onClick={() => setPageAndSort({ ...pageAndSort, page: 0 })}
                    style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}>
                    <i className="bi bi-arrow-clockwise" />
                </button>

                <button disabled={exportingMDFes} className="icon-button action-button centered" style={{ marginLeft: '20px', width: '188px' }} onClick={() => handleExportMDFes()}>
                    {exportingMDFes ? (
                        <CircularProgress size={14} style={{ 'color': '#FFFFFF' }} />
                    ) : (
                        <>
                            <i className="bi bi-file-earmark-spreadsheet"></i>
                            <span>Exportar MDFes</span>
                        </>
                    )}
                </button>
            </div>
            <div style={{ display: 'flex', marginLeft: '6px', alignItems: 'center' }}>
                <span><b style={{ marginLeft: '4px', marginRight: '4px' }}>{totalAmountMDFes}</b> MDFes encontrados.</span>
                <div style={{ height: '25px', width: '1px', backgroundColor: 'var(--grey-light)', margin: '0 8px', opacity: '.5' }}></div>
                <span><b style={{ marginLeft: '4px', marginRight: '4px' }}>{selectedMDFes.length}</b> MDFes selecionados.</span>
                {customizeColumnsBtn}
            </div>
            <Table data={data}
                columns={columns}
                orderInfo={pageAndSort.sort}
                onSortByColumn={(col: Column) => handleSortByColumn(col)}
                actionsConfig={{ actions: actions, minWidth: 110 }}
                disableSelection={false}
                onSelectionChange={(mdfes: any) => { setSelectedMDFes(mdfes.filter((md: any) => md.isSelected)) }}
                handleFetchMore={() => setPageAndSort({ ...pageAndSort, page: (pageAndSort.page + 1) })}
                hasMore={hasMore}
                batchActions={[
                    {
                        label: 'Baixar XML',
                        actionCallback: handleDownloadXmlBatch,
                        isDisabled: selectedMDFes.length === 0
                    },
                    {
                        label: 'Baixar DAMDFE',
                        actionCallback: handleDownloadDamdfeBatch,
                        isDisabled: selectedMDFes.length === 0
                    },
                    {
                        label: 'Imprimir DAMDFE',
                        actionCallback: handleDownloadDamdfeMerged,
                        isDisabled: selectedMDFes.length === 0
                    }
                ]}
            />
        </section>

        {/* DIALOG - DOWNLOAD ARQUIVOS MDFE */}
        {
            !!activeMDFe && !!downloadOptionsOpen && <ModalFrame>
                <Modal
                    headerTitle="Baixar arquivos"
                    closeOnClickAway={true}
                    handleClose={() => setDownloadOptionsOpen(false)}
                    loading={loadingDownload}
                >
                    <span style={{ width: '50%' }}>Selecione a opção desejada:</span>

                    <div style={{ width: '100%', display: 'flex', marginTop: '14px', justifyContent: 'space-around' }}>
                        <Tooltip title={activeIsOffline ? "O XML de autorização fica disponível depois que o MDFe emitido em contingência for transmitido" : ""} placement="top">
                            <span>
                                <button disabled={downloadingXml || activeIsOffline} className="icon-button centered" style={{ marginRight: '12px' }}
                                    onClick={() => handleDownloadXml(activeMDFe)}
                                >
                                    {downloadingXml ? (<CircularProgress size={14} style={{ 'color': '#19ea81' }} />) : 'Baixar XML'}
                                </button>
                            </span>
                        </Tooltip>
                        <button
                            className="icon-button centered"
                            onClick={() => handleVisualizarDamdfeClick(activeMDFe)}
                        >
                            Visualizar DAMDFE
                        </button>
                    </div>
                </Modal>
            </ModalFrame>
        }

        {/* DIALOG - VISUALIZAR DAMDFE */}

        {visualizarDamdfeOpen && (
            <ModalFrame>
                <Modal
                    headerTitle="Visualizar DAMDFE"
                    closeOnClickAway={true}
                    handleClose={() => {
                        setVisualizarDamdfeOpen(false);
                        setPdf(undefined);
                        setPdfFilename(undefined);
                    }}
                    headerActions={[
                        {
                            icon: (<i title="Imprimir" className="bi bi-printer" style={{ fontSize: '1rem' }} />),
                            actionCallback: () => handlePrint(pdf)
                        },
                        {
                            icon: (<i title="Download" className="bi bi-download" style={{ fontSize: '1rem' }} />),
                            actionCallback: () => fileDownload(pdf, pdfFilename)
                        }
                    ]}
                    loading={false}>
                    <PDFViewer pdf={pdf} />
                </Modal>
            </ModalFrame>
        )}

        {/* DIALOG - CANCELAR MDFE */}
        {
            !!activeMDFe && !!cancelDialogOpen && <ModalFrame>
                <Modal
                    headerTitle="Cancelar MDFe"
                    closeOnClickAway={true}
                    handleClose={() => setCancelDialogOpen(false)}
                    loading={loadingCancel}
                    actionButtons={[{
                        text: 'Cancelar',
                        actionCallback: () => setCancelDialogOpen(false),
                        positiveAction: false
                    },
                    {
                        text: 'Confirmar',
                        actionCallback: () => handleCancelMDFe(activeMDFe),
                        positiveAction: true
                    }]}
                >
                    <div className="correcao-frame" key={"cancel"}>
                        <span>Digite a justificativa para o cancelamento do MDFe <b>{activeMDFe && activeMDFe.docNum}</b> :</span>
                        <textarea ref={justificationRef} placeholder="(Texto deve conter entre 15 e 255 caracteres)" />
                    </div>
                </Modal>
            </ModalFrame>
        }

        {/* DIALOG - ENCERRAR MDFE (no lugar da carta de correção do CT-e: o MDF-e não tem CC-e) */}
        {
            !!activeMDFe && !!closureDialogOpen && <ModalFrame className="enc-modal">
                <Modal
                    headerTitle="Encerrar MDFe"
                    closeOnClickAway={false}
                    handleClose={() => setClosureDialogOpen(false)}
                    loading={loadingEncerramento}
                    actionButtons={[{
                        text: 'Cancelar',
                        actionCallback: () => setClosureDialogOpen(false),
                        positiveAction: false
                    },
                    {
                        text: 'Encerrar',
                        actionCallback: () => handleCloseMDFe(activeMDFe),
                        positiveAction: true
                    }]}
                >
                    <div className="enc-frame">
                        <span>Informe os dados do encerramento do MDFe: <b>{activeMDFe && activeMDFe.docNum}</b></span>
                        <div className="enc-row">
                            <div>
                                <small>Data do encerramento</small>
                                <DatePicker
                                    value={closure.closureDate ? parse(closure.closureDate, 'yyyy-MM-dd', new Date()) : null}
                                    locale="pt-BR"
                                    dayPlaceholder="dd"
                                    monthPlaceholder="mm"
                                    yearPlaceholder="aaaa"
                                    calendarIcon={<i className="bi bi-calendar"></i>}
                                    clearIcon={null}
                                    onChange={(value: Date | null) => { const closureDate = value ? format(value, 'yyyy-MM-dd') : ''; setClosure(prev => ({ ...prev, closureDate })); }}
                                />
                            </div>
                            <div className="enc-uf">
                                <small>UF</small>
                                <PickList
                                    style={{ width: '80px' }}
                                    items={closureUFList}
                                    placeholder="UF"
                                    selected={[closureUFList.find(uf => uf.codigo === closure.stateCode)].filter(Boolean) as PickListItem[]}
                                    showEndMessage={false}
                                    allowDeselection={false}
                                    showSearch={false}
                                    itemKey={"closureUf"}
                                    handleApplySelection={(values: PickListItem[]) => {
                                        const stateCode = values.length > 0 ? values[0].codigo : '';
                                        /* a PickList reaplica a mesma seleção ao fechar: só troca o município quando a UF muda de fato */
                                        setClosure(prev => prev.stateCode === stateCode ? prev : {
                                            ...prev,
                                            stateCode,
                                            /* no exterior o município é fixo */
                                            cityCode: stateCode === COD_UF_EXTERIOR ? COD_MUN_EXTERIOR : ''
                                        });
                                    }} />
                            </div>
                            <div>
                                <small>Município do encerramento</small>
                                {
                                    (closure.stateCode && closure.stateCode !== COD_UF_EXTERIOR && !loadingCities && closureCities.length > 0)
                                        /* a key remonta a lista a cada UF: a PickList guarda os itens da busca local na montagem */
                                        ? <PickList
                                            key={`closureCity-${closure.stateCode}`}
                                            style={{ width: '260px' }}
                                            items={closureCities}
                                            placeholder="Selecione"
                                            selected={[closureCities.find(city => city.codigo === closure.cityCode)].filter(Boolean) as PickListItem[]}
                                            showEndMessage={false}
                                            allowDeselection={false}
                                            isAsync={false}
                                            itemKey={"closureCity"}
                                            handleApplySelection={(values: PickListItem[]) => {
                                                const cityCode = values.length > 0 ? values[0].codigo : '';
                                                setClosure(prev => prev.cityCode === cityCode ? prev : { ...prev, cityCode });
                                            }} />
                                        : <input type="text" className="enc-city-placeholder" disabled
                                            value={closure.stateCode === COD_UF_EXTERIOR ? `Exterior (${COD_MUN_EXTERIOR})`
                                                : loadingCities ? 'Carregando municípios...'
                                                : closure.stateCode ? 'Nenhum município encontrado' : 'Selecione a UF'} />
                                }
                            </div>
                        </div>
                    </div>
                </Modal>
            </ModalFrame>
        }

        {/* DIALOG - HISTÓRICO MDFE */}
        {
            mdfeHistoryDialogOpen && <ModalFrame>
                <Modal
                    headerTitle="Histórico do MDFe"
                    closeOnClickAway={false}
                    handleClose={() => setMdfeHistoryDialogOpen(false)}
                    loading={loadingHistory}
                >
                    <div style={{ display: 'table-table', border: `1px solid ${'var(--grey-light)'}`, borderRadius: '10px' }}>
                        <div style={{ display: 'table-row', fontWeight: 'bold', textAlign: "center" }}>
                            <div style={{ width: '150px', display: 'table-cell', padding: '10px' }}>Data</div>
                            <div style={{ display: 'table-cell' }}>Status</div>
                            <div style={{ minWidth: '100px', display: 'table-cell' }}>Usuário</div>
                        </div>
                        <div style={{ display: 'table-row' }}>
                            <hr className="line-separator" />
                            <hr className="line-separator" />
                            <hr className="line-separator" />
                        </div>
                        {
                            !!activeMDFe && !!activeMDFe.history && activeMDFe.history.map((histItem, index) => <div key={index} style={{ width: '100%', display: 'table-row', padding: '5px' }} >
                                <div className="div-table-cell" style={{ width: '150px' }}>
                                    {histItem.createdAt}
                                </div>
                                <div className="div-table-cell">
                                    <div className="hist-item-frame">
                                        <div>
                                            <Tooltip title={histItem.status}
                                                placement='top' >
                                                <div className="status-indicator" />
                                            </Tooltip>
                                            {
                                                (!!activeMDFe.history && index < activeMDFe.history.length - 1) ?
                                                    <p className="status-link-bar" ></p>
                                                    : ''
                                            }
                                        </div>
                                        <div style={{ paddingLeft: '15px' }}>
                                            <span style={{
                                                color: `${(index === 0 && histItem.status) ? 'var(--color-success)' : ''}`,
                                                fontSize: '100%',
                                                fontWeight: `${(index === 0 && histItem.status) ? 'bold' : ''}`
                                            }}>{histItem.message}</span>
                                            {histItem.details && !!histItem.details.length && (<HistoryDetails items={histItem.details} />)}
                                        </div>
                                    </div>
                                </div>
                                <div className="div-table-cell" style={{ paddingRight: '10px', textAlign: 'center' }}>
                                    {histItem.username}
                                </div>
                            </div>)
                        }
                    </div>
                </Modal>
            </ModalFrame>
        }

    </StyledFrame ></PageFrame >;
}

export default MDFeListPage;
